import { inject, Injectable } from '@angular/core';
import {
  CheckPoint,
  CreateCheckPointParams,
  UpdateCheckPointParams,
} from '../models/checkPoint.model';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Asset CRUD service using local SQLite database
 * All operations are 100% offline - no backend API calls
 */
@Injectable({
  providedIn: 'root',
})
export class CheckPointService {
  private db = inject(DatabaseService);

  private logger = inject(LoggerService).getLogger('checkpoints');

  /**
   * Parse checkpoint row from database (extracts name/description from data JSON column)
   */
  private parseCheckPointRow(row: any): CheckPoint {
    const data = row.data ? JSON.parse(row.data) : {};
    return {
      id: row.id,
      name: data.name || '',
      description: data.description,
      qr_code: row.qr_code,
      sample_scenario: data.sample_scenario || undefined,
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
  }

  /**
   * Get all checkpoints
   */
  async getAll(): Promise<CheckPoint[]> {
    await this.db.init();

    this.logger.debug('Getting all checkpoints from database...');
    const results = await this.db.exec('SELECT * FROM assets ORDER BY created_at DESC');
    this.logger.debug('Raw query results:', results);
    this.logger.debug('Number of checkpoints found:', results.length);

    return results.map((row) => this.parseCheckPointRow(row));
  }

  /**
   * Create a reactive query for all assets
   * Returns signals that automatically update when data changes via sync
   */
  createQuery(): QuerySignal<CheckPoint[]> {
    return this.db.createQuery('assets', () => this.getAll());
  }

  /**
   * Create a reactive query for a single asset by ID
   * Returns signals that automatically update when data changes via sync
   */
  createQueryById(id: string): QuerySignal<CheckPoint | null> {
    return this.db.createQuery('assets', () => this.getById(id));
  }

  /**
   * Get asset by ID
   */
  async getById(id: string): Promise<CheckPoint | null> {
    await this.db.init();

    const results = await this.db.exec('SELECT * FROM assets WHERE id = ?', [id]);

    return results.length > 0 ? this.parseCheckPointRow(results[0]) : null;
  }

  /**
   * Get asset by QR code
   */
  async getByQrCode(qrCode: string): Promise<CheckPoint | null> {
    await this.db.init();

    const results = await this.db.exec('SELECT * FROM assets WHERE qr_code = ?', [qrCode]);

    return results.length > 0 ? this.parseCheckPointRow(results[0]) : null;
  }

  /**
   * Search checkpoints by name or description within data JSON column
   */
  async search(searchTerm: string): Promise<CheckPoint[]> {
    await this.db.init();

    const results = await this.db.exec(
      `SELECT * FROM assets
       WHERE json_extract(data, '$.name') LIKE ?
          OR json_extract(data, '$.description') LIKE ?
       ORDER BY json_extract(data, '$.name')`,
      [`%${searchTerm}%`, `%${searchTerm}%`],
    );

    return results.map((row) => this.parseCheckPointRow(row));
  }

  /**
   * Create a new checkpoint
   * Generates ID client-side using nanoid for offline-first sync compatibility
   */
  async create(params: CreateCheckPointParams): Promise<CheckPoint> {
    try {
      await this.db.init();

      // Generate nanoid for the new checkpoint (required for offline-first sync)
      const id = this.db.generateId();

      this.logger.debug('Creating checkpoint with params:', params, 'ID:', id);

      // Build data JSON object with name and description
      const data: any = { name: params.name };
      if (params.description !== undefined) {
        data.description = params.description;
      }
      if (params.sample_scenario) {
        data.sample_scenario = params.sample_scenario;
      }

      // Insert the checkpoint with data JSON column
      await this.db.run(
        `INSERT INTO assets (id, qr_code, data, created_by, created_at, updated_by, updated_at)
         VALUES (?, ?, ?, '', ?, '', ?)`,
        [id, params.qr_code || '', JSON.stringify(data), Date.now(), Date.now()],
      );

      // Retrieve the inserted checkpoint
      const checkPoint = await this.getById(id);

      if (!checkPoint) {
        this.logger.error('Checkpoint was inserted but could not be retrieved. ID:', id);
        throw new Error('Failed to create checkpoint');
      }

      this.logger.debug('Checkpoint created successfully:', checkPoint);
      return checkPoint;
    } catch (error) {
      this.logger.error('Checkpoint creation failed:', {
        params,
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  }

  /**
   * Update an existing checkpoint
   */
  async update(id: string, params: UpdateCheckPointParams): Promise<CheckPoint> {
    await this.db.init();

    // Get existing asset to merge data
    const existing = await this.getById(id);
    if (!existing) {
      throw new Error('Checkpoint not found');
    }

    // Build update query dynamically based on provided params
    const updates: string[] = [];
    const values: any[] = [];

    // Handle data JSON column updates
    if (params.name !== undefined || params.description !== undefined) {
      const data: any = {
        name: params.name !== undefined ? params.name : existing.name,
      };
      if (params.description !== undefined || existing.description !== undefined) {
        data.description =
          params.description !== undefined ? params.description : existing.description;
      }
      // Keep the onboarding sample tag so "remove sample data" still finds it.
      if (existing.sample_scenario) {
        data.sample_scenario = existing.sample_scenario;
      }
      updates.push('data = ?');
      values.push(JSON.stringify(data));
    }

    // Handle qr_code column update
    if (params.qr_code !== undefined) {
      updates.push('qr_code = ?');
      values.push(params.qr_code);
    }

    updates.push('updated_at = ?');
    values.push(Date.now());
    values.push(id);

    await this.db.run(`UPDATE assets SET ${updates.join(', ')} WHERE id = ?`, values);

    const checkPoint = await this.getById(id);
    if (!checkPoint) {
      throw new Error('Checkpoint not found after update');
    }

    return checkPoint;
  }

  /**
   * Delete an asset
   */
  async delete(id: string): Promise<void> {
    await this.db.init();

    await this.db.run('DELETE FROM assets WHERE id = ?', [id]);
  }

  /**
   * Generate a unique QR code for an asset
   * Returns a URL that points to the record interaction flow
   */
  generateQrCode(formId: string): string {
    // Generate URL for recording form-submissions with this asset
    // In production, this would include the full domain
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    return `${baseUrl}/scan/${formId}`;
  }

  /**
   * Count checkpoints toward the plan limit. Onboarding sample checkpoints are
   * excluded so trying a sample scenario never blocks creating real ones.
   */
  async count(): Promise<number> {
    await this.db.init();

    const results = await this.db.exec(
      `SELECT COUNT(*) as count FROM assets WHERE json_extract(data, '$.sample_scenario') IS NULL`,
    );
    return results[0]?.count || 0;
  }

  /**
   * Get assets assigned to a specific interaction type
   */
  async getByFormId(formId: string): Promise<CheckPoint[]> {
    await this.db.init();

    const results = await this.db.exec(
      `SELECT a.*
       FROM assets a
       INNER JOIN asset_interaction_types ait ON a.id = ait.asset_id
       WHERE ait.interaction_type_id = ?
       ORDER BY json_extract(a.data, '$.name')`,
      [formId],
    );

    return results.map((row) => this.parseCheckPointRow(row));
  }

  /**
   * Create a reactive query for assets assigned to an interaction type
   * Returns signals that automatically update when data changes via sync
   */
  createQueryByFormId(formId: string): QuerySignal<CheckPoint[]> {
    return this.db.createQuery('asset_interaction_types', () => this.getByFormId(formId));
  }

  /**
   * Get forms assigned to an asset
   */
  async getForms(checkPointId: string): Promise<string[]> {
    await this.db.init();

    const results = await this.db.exec(
      'SELECT interaction_type_id FROM asset_interaction_types WHERE asset_id = ?',
      [checkPointId],
    );

    return results.map((row) => row.interaction_type_id as string);
  }

  /**
   * Assign forms to an asset.
   * Uses a diff-based approach: only inserts new pairs, only deletes removed ones.
   * Avoids the DELETE+reinsert race where sync can re-insert old rows between the
   * DELETE and the new INSERT, creating duplicate (asset_id, interaction_type_id) pairs.
   */
  async assignForms(checkPointId: string, interactionTypeIds: string[]): Promise<void> {
    await this.db.init();

    const current = await this.getForms(checkPointId);
    const desired = new Set(interactionTypeIds);
    const existing = new Set(current);

    // Delete only removed assignments
    for (const formId of existing) {
      if (!desired.has(formId)) {
        await this.db.run(
          'DELETE FROM asset_interaction_types WHERE asset_id = ? AND interaction_type_id = ?',
          [checkPointId, formId],
        );
      }
    }

    // Insert only new assignments (INSERT OR IGNORE handles any duplicate that slipped through)
    const now = Date.now();
    for (const formId of desired) {
      if (!existing.has(formId)) {
        const id = this.db.generateId();
        await this.db.run(
          `INSERT OR IGNORE INTO asset_interaction_types (id, asset_id, interaction_type_id, created_by, created_at, updated_by, updated_at)
           VALUES (?, ?, ?, '', ?, '', ?)`,
          [id, checkPointId, formId, now, now],
        );
      }
    }
  }
}
